import { useState, useEffect } from "react";
import { Plus, Edit2, Trash2, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import axios from "axios";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import { useLanguage } from "@/contexts/LanguageContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ShiftTypesPage({ onLogout }) {
  const { t } = useLanguage();
  const [shiftTypes, setShiftTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [formData, setFormData] = useState({ 
    name: "", 
    start_time: "", 
    end_time: "", 
    color: "blue",
    is_custom: true
  });

  const colorOptions = [
    { value: 'orange', label: 'Orange' },
    { value: 'blue', label: 'Blau' },
    { value: 'indigo', label: 'Indigo' },
    { value: 'green', label: 'Grün' },
    { value: 'purple', label: 'Lila' },
    { value: 'red', label: 'Rot' },
    { value: 'yellow', label: 'Gelb' },
    { value: 'pink', label: 'Rosa' },
    { value: 'teal', label: 'Türkis' },
    { value: 'gray', label: 'Grau' }
  ];

  useEffect(() => {
    loadShiftTypes();
  }, []);

  const loadShiftTypes = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/shift-types`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShiftTypes(response.data);
    } catch (error) {
      toast.error("Fehler beim Laden der Schichttypen");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };

    try {
      if (editingType) {
        await axios.put(`${API}/shift-types/${editingType.id}`, formData, { headers });
        toast.success("Schichttyp aktualisiert!");
      } else {
        await axios.post(`${API}/shift-types`, formData, { headers });
        toast.success("Schichttyp hinzugefügt!");
      }
      loadShiftTypes();
      handleCloseModal();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Speichern");
    }
  };

  const handleDelete = async (typeId, isCustom) => {
    if (!isCustom) {
      toast.error("Standard-Schichttypen können nicht gelöscht werden");
      return;
    }
    
    if (!window.confirm("Sind Sie sicher, dass Sie diesen Schichttyp löschen möchten?")) return;

    const token = localStorage.getItem('token');
    try {
      await axios.delete(`${API}/shift-types/${typeId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Schichttyp gelöscht!");
      loadShiftTypes();
    } catch (error) {
      toast.error("Fehler beim Löschen des Schichttyps");
    }
  };

  const handleEdit = (type) => {
    setEditingType(type);
    setFormData({ 
      name: type.name, 
      start_time: type.start_time, 
      end_time: type.end_time,
      color: type.color,
      is_custom: type.is_custom
    });
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setEditingType(null);
    setFormData({ name: "", start_time: "", end_time: "", color: "blue", is_custom: true });
  };

  const colorClasses = {
    orange: 'bg-orange-100 text-orange-800 border-orange-300',
    blue: 'bg-blue-100 text-blue-800 border-blue-300',
    indigo: 'bg-indigo-100 text-indigo-800 border-indigo-300',
    gray: 'bg-gray-100 text-gray-800 border-gray-300',
    green: 'bg-green-100 text-green-800 border-green-300',
    purple: 'bg-purple-100 text-purple-800 border-purple-300',
    red: 'bg-red-100 text-red-800 border-red-300',
    yellow: 'bg-yellow-100 text-yellow-800 border-yellow-300',
    pink: 'bg-pink-100 text-pink-800 border-pink-300',
    teal: 'bg-teal-100 text-teal-800 border-teal-300'
  };

  return (
    <Layout onLogout={onLogout}>
      <div className="p-6" data-testid="shift-types-page">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {t("shift_types_title")}
            </h1>
            <p className="text-slate-600 mt-1">{t("configure_schedules")}</p>
          </div>
          <Button
            onClick={() => setModalOpen(true)}
            className="bg-slate-900 hover:bg-slate-800"
            data-testid="add-shift-type-button"
          >
            <Plus className="w-5 h-5 mr-2" />
            Schichttyp hinzufügen
          </Button>
        </div>

        {loading ? (
          <div className="text-center py-12 text-slate-600">{t("loading")}</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {shiftTypes.map(type => (
              <div
                key={type.id}
                className={`border-2 rounded-lg p-5 shadow-sm hover:shadow-md transition-shadow ${colorClasses[type.color] || colorClasses.gray}`}
                data-testid={`shift-type-card-${type.id}`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <h3 className="font-bold text-lg mb-1">{type.name}</h3>
                    <div className="flex items-center gap-2 text-sm" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                      <Clock className="w-4 h-4" />
                      <span>{type.start_time} - {type.end_time}</span>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleEdit(type)}
                      className="text-slate-700 hover:text-slate-900"
                      data-testid={`edit-shift-type-${type.id}`}
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    {type.is_custom && (
                      <button
                        onClick={() => handleDelete(type.id, type.is_custom)}
                        className="text-red-700 hover:text-red-900"
                        data-testid={`delete-shift-type-${type.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
                {!type.is_custom && (
                  <div className="text-xs opacity-60 mt-2">Standard-Schichttyp</div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={modalOpen} onOpenChange={handleCloseModal}>
        <DialogContent data-testid="shift-type-modal">
          <DialogHeader>
            <DialogTitle>
              {editingType ? t("edit_shift_type") : t("add_shift_type")}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                placeholder="z.B. Abendschicht"
                data-testid="shift-type-name-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="start_time">{t("start_time")} *</Label>
                <Input
                  id="start_time"
                  type="time"
                  value={formData.start_time}
                  onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                  required
                  data-testid="shift-type-start-input"
                />
              </div>
              <div>
                <Label htmlFor="end_time">{t("end_time")} *</Label>
                <Input
                  id="end_time"
                  type="time"
                  value={formData.end_time}
                  onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                  required
                  data-testid="shift-type-end-input"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="color">{t("color")} *</Label>
              <Select
                value={formData.color}
                onValueChange={(value) => setFormData({ ...formData, color: value })}
              >
                <SelectTrigger id="color" data-testid="shift-type-color-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {colorOptions.map(option => (
                    <SelectItem key={option.value} value={option.value}>
                      <div className="flex items-center gap-2">
                        <div className={`w-4 h-4 rounded ${colorClasses[option.value]}`} />
                        {option.label}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2 justify-end">
              <Button type="button" variant="outline" onClick={handleCloseModal} data-testid="cancel-shift-type-button">
                {t("cancel")}
              </Button>
              <Button type="submit" className="bg-slate-900 hover:bg-slate-800" data-testid="save-shift-type-button">
                {editingType ? t("update") : t("add")}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}